import type { DocsDocumentSummary, DocsHostContext } from '@/bridge/types';
import { DocsApp } from '@/DocsApp';
import {
  Alert,
  App,
  Button,
  ConfigProvider,
  Empty,
  Skeleton,
  theme as antdTheme,
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { docsApi, type DocumentSession } from './api';
import { createStandaloneBridge } from './bridge';
import { DocumentCenter } from './DocumentCenter';
import {
  docsPath,
  parseRoute,
  type DocsRoute,
} from './routing';
import './standalone.css';

const DOCUMENT_HOME_KEY = 'posbi-package-pospal-bi-general';
const guestSession: DocumentSession = {
  authenticated: false,
  admin: false,
};

function findDocumentHome(items: DocsDocumentSummary[]) {
  const priority = (item: DocsDocumentSummary) => {
    const title = item.title.trim();
    const path = (item.breadcrumb || []).join('/');
    if (item.slug === DOCUMENT_HOME_KEY) return 0;
    if (title.includes('产品介绍')) return 1;
    if (title.includes('介绍')) return 2;
    if (title.includes('产品总则')) return 3;
    if (title.includes('总则')) return 4;
    if (path.includes('介绍')) return 5;
    if (path.includes('总则')) return 6;
    return 7;
  };
  return [...items].sort(
    (left, right) =>
      priority(left) - priority(right) ||
      left.title.localeCompare(right.title, 'zh-CN'),
  )[0];
}

interface PublicHomeProps {
  onOpenDocument(documentId: string, replace?: boolean): void;
}

function PublicHome({ onOpenDocument }: PublicHomeProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const documents = await docsApi.listDocuments();
      const home = findDocumentHome(documents);
      if (home) onOpenDocument(home.id, true);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '文档加载失败');
    } finally {
      setLoading(false);
    }
  }, [onOpenDocument]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="docs-standalone-state">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="docs-standalone-state">
        <Alert
          showIcon
          type="error"
          message="文档加载失败"
          description={error}
          action={<Button onClick={() => void load()}>重试</Button>}
        />
      </div>
    );
  }
  return (
    <div className="docs-standalone-state">
      <Empty description="暂无已发布文档" />
    </div>
  );
}

interface DocumentRouteProps {
  route: Extract<DocsRoute, { type: 'document' }>;
  navigate(url: string, replace?: boolean): void;
}

function DocumentRoute({ navigate, route }: DocumentRouteProps) {
  const context = useMemo<DocsHostContext>(
    () => ({
      documentId: route.documentId,
      revisionId: route.revisionId,
      targetBlockId: route.targetBlockId,
      variant: 'page',
    }),
    [route.documentId, route.revisionId, route.targetBlockId],
  );
  const bridge = useMemo(
    () =>
      createStandaloneBridge(context, {
        navigateDocument(documentId, targetBlockId, revisionId) {
          navigate(
            `${docsPath(encodeURIComponent(documentId))}${
              revisionId ? `?revisionId=${encodeURIComponent(revisionId)}` : ''
            }${targetBlockId ? `#${encodeURIComponent(targetBlockId)}` : ''}`,
          );
        },
        navigateHome() {
          navigate(docsPath());
        },
        goBack() {
          if (window.history.length > 1) {
            window.history.back();
          } else {
            navigate(docsPath());
          }
        },
      }),
    [context, navigate],
  );

  return (
    <DocsApp
      key={`${route.documentId}:${route.revisionId || ''}`}
      bridge={bridge}
      initialContext={context}
    />
  );
}

function StandaloneRouter() {
  const [route, setRoute] = useState<DocsRoute>(() => parseRoute());
  const [session, setSession] = useState<DocumentSession>();
  const [sessionError, setSessionError] = useState('');

  const navigate = useCallback((url: string, replace = false) => {
    if (replace) window.history.replaceState(null, '', url);
    else window.history.pushState(null, '', url);
    setRoute(parseRoute());
  }, []);

  useEffect(() => {
    const handlePopState = () => setRoute(parseRoute());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (route.type === 'login') {
      navigate(docsPath(), true);
    }
  }, [navigate, route.type]);

  useEffect(() => {
    let canceled = false;
    const loadSession = () => {
      docsApi
        .getSession()
        .then(nextSession => {
          if (!canceled) {
            setSession(nextSession);
            setSessionError('');
          }
        })
        .catch(error => {
          if (!canceled) {
            setSession(guestSession);
            setSessionError(
              error instanceof Error ? error.message : '登录状态检查失败',
            );
          }
        });
    };
    const handleLoggedInUserChange = (event: StorageEvent) => {
      if (event.key === 'LOGGED_IN_USER') {
        loadSession();
      }
    };
    loadSession();
    window.addEventListener('storage', handleLoggedInUserChange);
    return () => {
      canceled = true;
      window.removeEventListener('storage', handleLoggedInUserChange);
    };
  }, []);

  const openDocument = useCallback(
    (documentId: string, replace = false) => {
      navigate(docsPath(encodeURIComponent(documentId)), replace);
    },
    [navigate],
  );

  if (!session) {
    return (
      <div className="docs-standalone-state">
        <Skeleton active paragraph={{ rows: 9 }} />
      </div>
    );
  }

  if (route.type === 'login') {
    return (
      <div className="docs-standalone-state">
        <Skeleton active paragraph={{ rows: 9 }} />
      </div>
    );
  }

  if (route.type === 'document') {
    return <DocumentRoute navigate={navigate} route={route} />;
  }

  if (route.type === 'home') {
    return session.admin ? (
      <DocumentCenter onOpenDocument={openDocument} />
    ) : (
      <>
        {sessionError ? (
          <Alert
            banner
            showIcon
            closable
            type="warning"
            message={`按公开只读模式访问：${sessionError}`}
          />
        ) : null}
        <PublicHome onOpenDocument={openDocument} />
      </>
    );
  }

  return (
    <div className="docs-standalone-state">
      <Empty description="页面不存在">
        <Button type="primary" onClick={() => navigate(docsPath(), true)}>
          返回文档首页
        </Button>
      </Empty>
    </div>
  );
}

export function StandaloneApp() {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: { colorPrimary: '#1b9aee', borderRadius: 4 },
      }}
    >
      <App className="docs-standalone-app">
        <StandaloneRouter />
      </App>
    </ConfigProvider>
  );
}
